import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";
import { getFundingProgress } from "@/api/cfoFunding";
import type { CfoFundingProgress } from "@/api/types";
import { CFO_DECISIONS } from "@/lib/cfoLabels";
import { cn } from "@/lib/utils";
import { useFundingImportStore } from "@/stores/fundingImport.store";
import { RUNNING_LABEL, countOf, currentOf, etaLabel, failedOf, overallOf } from "./fundingProgress";

const POLL_MS = 2500;
const RETRY_MS = 10_000;

/**
 * The funding import that continues in the background, at the bottom right of every
 * page (owner, 2026-09-28). A click reopens the import's window; the cross hides it
 * (the import itself continues). It stops asking once nothing is left to run.
 */
export function FundingImportDock() {
  const tracked = useFundingImportStore((s) => s.tracked);
  const dismiss = useFundingImportStore((s) => s.dismiss);
  const requestOpen = useFundingImportStore((s) => s.requestOpen);
  const viewing = useFundingImportStore((s) => s.viewing);
  const navigate = useNavigate();
  const [progress, setProgress] = useState<CfoFundingProgress | null>(null);
  const [tick, setTick] = useState(0);
  // The share shown never goes back: scoring work appears as the rounds are recorded.
  const peak = useRef({ importId: "", pct: 0 });

  const slug = tracked?.slug;
  const importId = tracked?.importId;
  // The import's window is open: it shows (and reads) all of this already.
  const hidden = Boolean(importId) && viewing === importId;
  useEffect(() => {
    setProgress(null);
  }, [slug, importId]);

  useEffect(() => {
    if (!slug || !importId || hidden) return;
    let live = true;
    let timer: number | undefined;
    getFundingProgress(slug, importId)
      .then((value) => {
        if (!live) return;
        setProgress(value);
        if (!value.complete) timer = window.setTimeout(() => setTick((n) => n + 1), POLL_MS);
      })
      .catch((error: unknown) => {
        if (!live) return;
        // The import is gone, or no longer readable by this user: nothing to follow.
        if (axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) {
          dismiss();
          return;
        }
        timer = window.setTimeout(() => setTick((n) => n + 1), RETRY_MS);
      });
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [slug, importId, hidden, tick, dismiss]);

  if (!tracked || hidden) return null;

  const complete = progress?.complete ?? false;
  const current = progress ? currentOf(progress) : null;
  const failure = progress ? failedOf(progress) : null;
  if (peak.current.importId !== tracked.importId) peak.current = { importId: tracked.importId, pct: 0 };
  const pct = progress ? Math.max(peak.current.pct, Math.round(overallOf(progress) * 100)) : 0;
  peak.current.pct = pct;
  const eta = progress && !complete ? etaLabel(progress.eta_seconds) : null;
  const decisions = progress
    ? Object.entries(progress.decisions)
        .sort((a, b) => b[1] - a[1])
        .map(([key, n]) => `${CFO_DECISIONS[key]?.label ?? key.replace(/_/g, " ").toLowerCase()} ${n}`)
        .join(" · ")
    : "";
  const Icon = !progress
    ? Loader2
    : progress.stalled || (complete && failure)
      ? AlertTriangle
      : complete
        ? CheckCircle2
        : Loader2;
  const tone = !progress
    ? "bg-indigo-50 text-indigo-600"
    : progress.stalled || (complete && failure)
      ? "bg-amber-50 text-amber-600"
      : complete
        ? "bg-emerald-50 text-emerald-600"
        : "bg-indigo-50 text-indigo-600";
  const line = !progress
    ? "Reading the progress…"
    : progress.stalled
      ? "Nothing has run for a minute: the engine may be stopped"
      : complete
        ? failure
          ? `Complete, but a step failed: ${failure.label.toLowerCase()}`
          : `Complete · ${progress.companies} companies scored`
        : current
          ? `${RUNNING_LABEL[current.key]} · ${countOf(current)}`
          : "Finishing…";

  function reopen() {
    if (!tracked) return;
    requestOpen(tracked.importId);
    navigate(
      `/projects/${encodeURIComponent(tracked.slug)}/companies?funding_import=${encodeURIComponent(tracked.importId)}`,
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-40 w-96 max-w-[calc(100vw-2rem)]">
      <div className="relative rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5">
        <button
          type="button"
          onClick={reopen}
          className="flex w-full items-start gap-3 rounded-xl p-3 pr-9 text-left transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          aria-label="Open the funding import"
        >
          <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full", tone)}>
            <Icon className={cn("h-4 w-4", Icon === Loader2 && "animate-spin")} />
          </span>
          <span className="min-w-0 flex-1 space-y-1">
            <span className="block truncate text-sm font-medium text-slate-900">
              Funding import · {tracked.filename}
            </span>
            <span className="block truncate text-xs text-slate-500">{line}</span>
            {!complete && (
              <span className="flex items-center gap-2 pt-0.5">
                <span className="block h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <span
                    className="block h-full rounded-full bg-indigo-600 transition-all duration-700"
                    style={{ width: `${Math.max(pct, 3)}%` }}
                  />
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                  {progress ? `${pct}%` : ""}
                  {eta ? ` · ${eta}` : ""}
                </span>
              </span>
            )}
            {complete && decisions && (
              <span className="block truncate text-xs text-slate-400">{decisions}</span>
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-2 top-2 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label={complete ? "Close" : "Hide (the import continues)"}
          title={complete ? "Close" : "Hide (the import continues)"}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
